import { CdkDrag, CdkDragDrop, CdkDragEnd, CdkDropList } from '@angular/cdk/drag-drop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { formatDate, Location } from '@angular/common';
import {
    Component,
    ElementRef,
    HostBinding,
    HostListener,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, concat, from, fromEvent, merge, Subscription } from 'rxjs';
import { debounceTime, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { Entity } from '../../../../classes/entity.class';
import { Leave } from '../../../../classes/leave';
import { Material } from '../../../../classes/material.class';
import { PlanningHasEntity } from '../../../../classes/planning-has-entity.class';
import { Planning } from '../../../../classes/planning.class';
import { PlanningFixed } from '../../../../classes/planningfixed.class';
import { RealisationHourtype } from '../../../../classes/realisation';
import { UserPlanning } from '../../../../classes/user-planning';
import { User } from '../../../../classes/user.class';
import { CodaltComponent } from '../../../../codalt.component';
import { SetPlanningFixedDialogComponent } from '../../../../pages/planning-overview/set-planning-fixed-dialog/set-planning-fixed-dialog.component';
import { FilterByEntityTypePipe } from '../../../../pipes/filter-by-entity-type.pipe';
import { AuthorisationService } from '../../../../services/auth/authorisation.service';
import { Features } from '../../../../services/auth/features';
import { EmployeeService } from '../../../../services/employee.service';
import { EntitiesService } from '../../../../services/entities/entities.service';
import { LeaveService } from '../../../../services/leave.service';
import { PlanningFixedService } from '../../../../services/planning/planning-fixed.service';
import { PlanningService } from '../../../../services/planning/planning.service';
import { UserPlanningService } from '../../../../services/planning/user-planning.service';
import { UserService } from '../../../../services/user/user.service';
import { LocalStorage } from '../../../../storage.class';
import {
    MapFilter,
    PlanningMapComponent,
} from '../../../../trackline/pages/trackline-planning/track-line-week/planning-map/planning-map.component';
import { Utils } from '../../../../utils.class';
import { TracklinePlanningEditDialogComponent } from '../../trackline-planning-edit-dialog/trackline-planning-edit-dialog.component';
import { TracklinePlanningEntityTypeDialogComponent } from '../../trackline-planning-entity-type-dialog/trackline-planning-entity-type-dialog.component';
import { DayEntityTypeFilterPipe } from '../../trackline-planning/day-entity-type-filter.pipe';
import { PlanningHighlightService } from '../../trackline-planning/planning-highlight.service';
import { TrackLineWeekDayComponent } from '../track-line-week-day/track-line-week-day.component';
import { TracklinePlanningDay } from '../trackline-planning-day';

@Component({
    selector: 'app-track-line-week',
    standalone: false,
    templateUrl: './track-line-week.component.html',
    styleUrls: ['./track-line-week.component.scss'],
})
export class TrackLineWeekComponent extends CodaltComponent implements OnInit, OnDestroy {
    @ViewChildren('day') dayComponents: TrackLineWeekDayComponent[];

    @ViewChild('planningMap') planningMap: PlanningMapComponent;
    @ViewChild('weekContainer') weekContainer: ElementRef<HTMLDivElement>;

    @HostBinding('class.need-overview-open') get needOverviewOpen() {
        return LocalStorage.needOverviewOpen;
    }

    set needOverviewOpen(open: boolean) {
        LocalStorage.needOverviewOpen = open;
    }

    print = false;
    mobile = false;
    readOnly = !AuthorisationService.hasFeature(Features.PlanningSaveSave);
    fromDate: Date;
    toDate: Date;
    week: number;
    year: number;
    weekPlanning: TracklinePlanningDay[];
    planningList: Planning[];
    leaves: { leave: Leave; users: User[] }[][];
    fixedUntil: Date;
    fixedDays: PlanningFixed[] = [];
    times: Date[];
    employees: User[];
    projectManagers: User[] = [];
    entities: Entity[];
    private entitiesSubscription: Subscription;
    someDayDragging = false;
    hoveredNeedId: number | null = null;
    focusNeedId: number | null = null;
    userPlannings: UserPlanning[];
    startHour = 4;
    hoursOnDay = 26;

    fcFilteredEntityTypes = new FormControl(LocalStorage.weekplanningTypeFilter ?? []);
    fcSearchNeed = new FormControl<string>('');
    fcProjectManager = new FormControl<number | null>(LocalStorage.weekplanningProjectManagerFilter);

    needOverview: Planning[] = [];
    needList: Planning[] = [];
    materialsById: Map<number, Material> = new Map();
    mapFilter: MapFilter = 'alle';
    planningsAndNeedsToShowOnMap: Planning[] = [];
    leaveDayUserMap: Map<string, Leave> = new Map<string, Leave>();

    protected readonly RealisationHourtype = RealisationHourtype;
    PlanningStatus = this.environment.status.Planning;

    private planningSubscriptions = new Subscription();
    private getDataSubscription?: Subscription;
    private autoScrolledCurrentWeek = false;

    constructor(
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private location: Location,
        private breakpointObserver: BreakpointObserver,
        private userService: UserService,
        private leaveService: LeaveService,
        private userPlanningService: UserPlanningService,
        private entitiesService: EntitiesService,
        private planningService: PlanningService,
        private planningFixedService: PlanningFixedService,
        private dialog: MatDialog,
        private elementRef: ElementRef,
        protected employeeService: EmployeeService,
        protected highlightService: PlanningHighlightService
    ) {
        super();
    }

    ngOnInit(): void {
        this.subscriptions.add(
            this.breakpointObserver.observe('(max-width: 722px), (pointer:coarse)').subscribe(match => {
                this.mobile = match.matches;
                this.readOnly = this.mobile || !AuthorisationService.hasFeature(Features.PlanningSaveSave);
            })
        );

        this.times = Array.from({ length: this.hoursOnDay }, (_, i) => {
            const d = new Date();
            d.setHours((this.startHour + i) % 24, 0, 0, 0);
            return d;
        });

        this.subscriptions.add(
            this.activatedRoute.params.subscribe((params: { week; year }) => {
                const monday = new Date();
                if (params?.year) {
                    monday.setFullYear(params.year);
                }
                Utils.setTime(monday, 0, 0);

                monday.setMonth(0);
                monday.setDate(1);
                monday.setDate(monday.getDate() - ((monday.getDay() % 7) - 1));
                const beginDate = new Date(monday);

                const week = +(params.week ?? formatDate(new Date(), 'w', 'nl'));
                beginDate.setDate(beginDate.getDate() + 7 * (week - 1));

                const endDate = Utils.setTime(new Date(beginDate), this.startHour, 59);
                endDate.setDate(endDate.getDate() + 7);

                this.week = week;
                this.year = week > 1 ? beginDate.getFullYear() : endDate.getFullYear();

                this.fromDate = beginDate;
                this.toDate = endDate;

                this.getData();
            })
        );

        this.subscriptions.add(
            merge(
                this.fcFilteredEntityTypes.valueChanges.pipe(
                    tap(types => {
                        LocalStorage.weekplanningTypeFilter = types;
                        this.setEmployeeDivMargins();
                    })
                ),
                this.fcProjectManager.valueChanges.pipe(
                    tap(projectManagerId => {
                        LocalStorage.weekplanningProjectManagerFilter = projectManagerId;
                    })
                )
            ).subscribe(() => {
                this.autoScrolledCurrentWeek = false;
                this.updateMap();
                setTimeout(() => {
                    this.setEqualHeights();
                    this.scrollToCurrentDayIfCurrentWeek();
                });
            })
        );

        this.subscriptions.add(
            this.userService.getByType(environment.projectmanager).subscribe(users => (this.projectManagers = users))
        );

        this.subscriptions.add(
            this.planningService.materials$.subscribe(materials => {
                this.materialsById = new Map(materials.map(material => [material.id, material]));
            })
        );

        this.subscriptions.add(
            this.planningService.getListByStatus(this.PlanningStatus.PlanningBehoefte).subscribe(planning => {
                this.needOverview = this.sortNeedListByExecuteAfterDate(planning);

                this.needList = structuredClone(this.needOverview);

                this.updateMap();
            })
        );

        combineLatest([this.activatedRoute.url, this.activatedRoute.params]).subscribe(([url, params]) => {
            if (params.planningId) {
                this.planningSubscriptions.add(
                    this.planningService.getSingle(+params.planningId).subscribe(planning => {
                        if (this.dialog.openDialogs?.length === 0) {
                            this.openPlanning(planning);
                        }
                    })
                );
                url.splice(-1, 1);
                this.location.replaceState(url.map(u => u.path).join('/'));
            }
        });

        this.subscriptions.add(
            this.fcSearchNeed.valueChanges.pipe(debounceTime(300)).subscribe(() => {
                this.updateMap();
            })
        );

        this.onScrollSetEmployeeDivMargin();
    }

    private sortNeedListByExecuteAfterDate(list: Planning[]): Planning[] {
        const twoWeeksFromNow = Date.now() + 14 * 86400000;
        const sortKey = (planning: Planning) =>
            new Date(planning.data?.execute_after_date ?? twoWeeksFromNow).getTime();

        return list.sort((a, b) => sortKey(a) - sortKey(b));
    }

    updateMap() {
        const needItems = new FilterByEntityTypePipe().transform(
            this.needList,
            this.fcFilteredEntityTypes.value,
            this.fcSearchNeed.value,
            this.materialsById,
            this.fcProjectManager.value
        );

        const planningItems =
            this.weekPlanning?.flatMap(
                p =>
                    new DayEntityTypeFilterPipe().transform(
                        p,
                        this.fcFilteredEntityTypes.value,
                        this.fcProjectManager.value
                    ).items
            ) ?? [];

        this.planningsAndNeedsToShowOnMap = [...needItems, ...planningItems];
    }

    filterNeedsByMap(visiblePlanningIds: number[]) {
        // Behoefte pins zijn niet zichtbaar op de kaart in de 'gepland'-weergave, en als er niets in
        // de viewbox valt (bv. data nog niet geladen, of er is niets zichtbaar) zegt die niets
        // zinnigs over welke behoeftes getoond moeten worden: toon dan de volledige lijst.
        this.needOverview =
            this.mapFilter === 'gepland' || !visiblePlanningIds.length
                ? this.needList
                : this.needList.filter(p => visiblePlanningIds.includes(p.id));
    }

    get visibleNeedOverview(): Planning[] {
        // Bij een actieve zoekopdracht altijd alle behoeftes tonen, ook buiten de huidige
        // kaartweergave/-filter, zonder de kaart zelf te laten springen.
        if (this.fcSearchNeed.value) {
            return this.needList;
        }

        return this.needOverview;
    }

    onMapFilterChange(filter: MapFilter) {
        this.mapFilter = filter;
    }

    onBehoefteButtonClick(event: MouseEvent) {
        if (event.ctrlKey || event.metaKey || event.button === 1) {
            this.openBehoefteInNewTab();
            return;
        }

        this.needOverviewOpen = !this.needOverviewOpen;
    }

    openBehoefteInNewTab() {
        const url = this.router.serializeUrl(this.router.createUrlTree(['/behoefte']));
        Utils.openTab(url);
    }

    onPlanningHover(planning: Planning | null) {
        this.highlightService.setHovered(planning?.id ?? null);
        this.hoveredNeedId = planning?.id ?? null;
    }

    focusOnMapId(planningId: number) {
        if (!this.needOverviewOpen) {
            this.needOverviewOpen = true;
        }

        window.setTimeout(() => {
            this.planningMap.focus(planningId);
        }, 0);
    }

    private onScrollSetEmployeeDivMargin() {
        const weekElement = this.elementRef.nativeElement.querySelector('.week');
        this.subscriptions.add(
            fromEvent(weekElement, 'scroll').subscribe(() => {
                this.setEmployeeDivMargins();
            })
        );
    }

    private setEmployeeDivMargins() {
        const container = this.weekContainer.nativeElement;
        let prevElementsWidth = 0;
        document.querySelectorAll('.day:not(.times-day)').forEach(day => {
            const dayElement = day as HTMLElement;
            const isVisible =
                dayElement.offsetLeft < container.scrollLeft + container.clientWidth &&
                dayElement.offsetLeft + dayElement.offsetWidth > container.scrollLeft;
            if (dayElement.scrollWidth > window.innerWidth && isVisible) {
                const visibleWidth = dayElement.offsetWidth - (container.scrollLeft - prevElementsWidth);
                if (visibleWidth >= 400) {
                    const hiddenLeft = Math.max(0, container.scrollLeft - prevElementsWidth);
                    (dayElement.querySelector('.employees') as HTMLElement).style.marginLeft = `${hiddenLeft}px`;
                    (dayElement.querySelector('.date') as HTMLElement).style.marginLeft = `${hiddenLeft}px`;
                }
            }
            prevElementsWidth += dayElement.offsetWidth;
        });
    }

    next() {
        let week = this.week + 1;
        let year = this.year;
        if (week > 52) {
            week = 1;
            year += 1;
        }
        this.router.navigate([`weekplanning`, week, year]);
    }

    prev() {
        let week = this.week - 1;
        let year = this.year;
        if (week < 1) {
            week = 52;
            year -= 1;
        }
        this.router.navigate([`weekplanning`, week, year]);
    }

    onDateSelected(event: any) {
        const date = new Date(event.value);
        const week = Utils.getWeek(date);
        this.router.navigate([`weekplanning`, week, date.getFullYear()]);
    }

    mayDrop(drag: CdkDrag<UserPlanning>, drop: CdkDropList<TracklinePlanningDay>) {
        return new Date(drag.data.begindate).getDate() == drop.data.date.getDate();
    }

    drop(event: CdkDragDrop<TracklinePlanningDay>) {
        if (event.container.id !== event.previousContainer.id) {
            if (event.item.data.entitytype_key) {
                const planningHas = event.item.data as PlanningHasEntity;
                if (
                    planningHas.entitytype_key.includes('hiring') ||
                    this.entities.some(
                        e => e.driver_user_id === planningHas.driver_user_id && e.id === planningHas.entity_id
                    )
                ) {
                    this.planningService.deleteSingle(planningHas);
                } else {
                    const planning = this.planningList.find(p => p.id === event.item.data.planning_id);
                    const ph = planning.planning_has.find(up => up.id === planningHas.id);
                    ph.driver_user_id = null;
                    this.subscriptions.add(this.planningService.save(planning).subscribe());
                }
            } else {
                this.subscriptions.add(
                    this.userPlanningService.deleteUserPlanning(event.item.data.id).subscribe({
                        next: () => {
                            const userPlannings = event.previousContainer.data['user_planning'];

                            userPlannings.splice(
                                userPlannings.findIndex((up: UserPlanning) => up.user_id === event.item.data.user_id),
                                1
                            );
                        },
                        error: () => {},
                    })
                );
            }
        }
    }

    toggleLeave(user: User, day: Date, hourtype: RealisationHourtype) {
        this.employeeService.addOrRemoveLeave(
            user,
            day,
            this.leaves.flat().map(l => l.leave),
            [],
            this.leaveDayUserMap,
            this.employees,
            [],
            hourtype,
            null,
            `Weekplanning - ${formatDate(day, 'dd-MM-yyyy', 'nl')}`,
            true,
            false,
            false,
            (leavesToSave: Leave[]) => {
                const saveCalls = [];

                leavesToSave.forEach(leave => {
                    saveCalls.push(
                        from(this.leaveService.saveLeave({ ...leave, users: leave.users.map(u => u.id) })).pipe(
                            tap(p => {
                                leave.id = p.data.id;
                                leave.updated_at = p.data.updated_at;
                            })
                        )
                    );
                });

                concat(...saveCalls).subscribe({ error: err => console.error(err) });
            }
        );
    }

    @HostListener('window:resize')
    protected setEqualHeights() {
        const employeeDivs = document.querySelectorAll('.day > .employees');
        let maxHeight = 0;

        employeeDivs.forEach((div: HTMLElement) => {
            div.style.height = 'auto';
            div.style.minWidth = `auto`;
            if (div.offsetHeight > 240) {
                div.style.minWidth = `420px`;
            } else {
                div.style.width = `auto`;
            }
            maxHeight = Math.max(maxHeight, div.offsetHeight);
        });

        employeeDivs.forEach((div: HTMLElement) => {
            div.style.height = `${maxHeight}px`;
        });
    }

    getData() {
        this.autoScrolledCurrentWeek = false;
        this.getDataSubscription?.unsubscribe();
        const fromDate = new Date(this.fromDate);

        fromDate.setDate(fromDate.getDate() - 1);

        const planning$ = this.planningService.getFilteredList(fromDate, this.toDate);
        const employees$ = this.userService.getByType(environment.weekPlanningEmployees);
        this.entitiesSubscription?.unsubscribe();
        this.entitiesSubscription = planning$
            .pipe(
                switchMap(planning =>
                    this.entitiesService.getList(
                        this.fromDate,
                        this.toDate,
                        planning
                            .flatMap(item => Utils.planningAllEntities(item, true))
                            .flatMap(ph => [ph.entity_id, ph.truck_entity_id, ph.lowloader_entity_id])
                            .filter(id => !!id)
                    )
                )
            )
            .subscribe(entities => (this.entities = entities));
        this.subscriptions.add(this.entitiesSubscription);

        const leave$ = this.leaveService.getFilteredList(this.fromDate, this.toDate);
        const fixed$ = this.planningFixedService.getLatestFixedUntilItem();
        const fixedDays$ = this.planningFixedService.getFixedDays();
        const fixedItems$ = this.planningFixedService.getFilteredList(this.fromDate, this.toDate);

        this.getDataSubscription = combineLatest([
            planning$,
            employees$,
            leave$,
            fixed$,
            fixedDays$,
            fixedItems$,
        ]).subscribe(([planning, employeeList, leaves, fixed, fixedDays, fixedItems]) => {
            this.fixedUntil = new Date(fixed.fixeduntil);
            this.fixedDays = fixedDays;
            this.planningList = planning;
            this.employees = employeeList;

            this.userPlannings = [...planning.flatMap(p => p.user_planning)];

            const grouped = new Map<string, Planning[]>();

            for (const item of planning) {
                if (item.mainPlanning && item.mainPlanning.begindate) {
                    const beginDate = new Date(item.mainPlanning.begindate);
                    const endDate = new Date(item.mainPlanning.enddate);
                    if (beginDate >= this.fromDate && beginDate <= this.toDate) {
                        if (
                            Utils.setTime(new Date(endDate), 6, 0, 0) < endDate ||
                            endDate.getDate() !== beginDate.getDate()
                        ) {
                            if (!grouped.has(beginDate.toDateString())) {
                                grouped.set(beginDate.toDateString(), []);
                            }
                            grouped.get(beginDate.toDateString()).push(item);
                        }
                        if (beginDate.getDate() !== endDate.getDate()) {
                            if (endDate > Utils.setTime(new Date(endDate), 6, 0, 0)) {
                                if (!grouped.has(endDate.toDateString())) {
                                    grouped.set(endDate.toDateString(), []);
                                }
                                grouped.get(endDate.toDateString()).push(item);
                            }
                        }
                        if (beginDate.getHours() <= this.startHour) {
                            // view on the previous day
                            const prevDayBeginDate = new Date(beginDate);
                            prevDayBeginDate.setDate(prevDayBeginDate.getDate() - 1);
                            if (!grouped.has(prevDayBeginDate.toDateString())) {
                                grouped.set(prevDayBeginDate.toDateString(), []);
                            }
                            grouped.get(prevDayBeginDate.toDateString()).push(item);
                        }
                    }
                }
            }

            // Ensure all days in the week are included, even if empty
            const allDays: TracklinePlanningDay[] = [];
            const currentDate = new Date(this.fromDate);

            while (currentDate <= this.toDate) {
                if (!Utils.matchesDate(currentDate, this.toDate)) {
                    const dateStr = currentDate.toDateString();
                    const items = grouped.get(dateStr) || [];
                    const employees = employeeList;
                    const isFixed =
                        currentDate.getTime() <= new Date(fixed.fixeduntil).getTime() ||
                        Utils.isSpecificFixedDay(currentDate, this.fixedDays);

                    allDays.push({
                        date: new Date(currentDate),
                        items,
                        employees,
                        fixed: isFixed,
                        fixedBy: isFixed ? Utils.planningFixedForDay(currentDate, fixedItems) : null,
                        leaves: this.leaveService.dayLeaves(leaves, employeeList, currentDate),
                    });
                }
                currentDate.setDate(currentDate.getDate() + 1);
            }

            this.weekPlanning = allDays;

            this.leaves = allDays.map(day => day.leaves);

            this.leavesToMap(leaves, employeeList);

            this.updateMap();

            setTimeout(() => {
                this.setEqualHeights();
                this.scrollToCurrentDayIfCurrentWeek();
            });
        });
    }

    private scrollToCurrentDayIfCurrentWeek(): void {
        const container = this.weekContainer?.nativeElement;
        if (
            container == null ||
            this.fromDate == null ||
            this.toDate == null ||
            !this.planningList?.length ||
            !this.employees?.length ||
            this.autoScrolledCurrentWeek
        ) {
            return;
        }
        this.autoScrolledCurrentWeek = true;
        const currentDate = new Date();
        if (currentDate < this.fromDate || currentDate > this.toDate) {
            container.scrollTo({ left: 0, behavior: 'instant' });
            return;
        }

        setTimeout(() => {
            const todayKey = Utils.formatDate(currentDate);
            const dayElement = container.querySelector<HTMLDivElement>(`[data-date="${todayKey}"]`);
            if (dayElement == null) {
                return;
            }
            container.scrollTo({ left: Math.max(0, dayElement.offsetLeft - 60), behavior: 'instant' });
        });
    }

    leavesToMap(leaves: Leave[], users: User[]) {
        leaves.forEach(leave => {
            this.employeeService.leaveToMap(this.leaveDayUserMap, leave, users);
        });
    }

    showInfoDialog(user: User, day: Date) {
        const userDay = this.weekPlanning.find(d => d.date.toDateString() === day.toDateString());

        this.employeeService.showInfoDialog(user, userDay);
    }

    openPlanning(planning: Planning, infoMessage?: string) {
        this.subscriptions.add(
            this.dialog
                .open(TracklinePlanningEditDialogComponent, {
                    disableClose: true,
                    panelClass: 'planning-edit-dialog',
                    data: { planning, infoMessage },
                })
                .afterClosed()
                .subscribe(() => {
                    const { begindate } = planning.mainPlanning;

                    if (begindate === null) {
                        return;
                    }

                    const week = Utils.getWeek(new Date(begindate));
                    const year = new Date(begindate).getFullYear();
                    if (this.week !== week || this.year !== year) {
                        this.router.navigateByUrl(`weekplanning/${week}/${year}`, { replaceUrl: true });
                    }
                })
        );
    }

    newPlanning() {
        if (this.fcFilteredEntityTypes.value.length === 1) {
            const mainEntity = this.entities.find(e =>
                e.entitytypes.some(et => et.key === this.fcFilteredEntityTypes.value[0])
            );

            this.dialog.open(TracklinePlanningEditDialogComponent, {
                disableClose: true,
                data: { mainEntity, statusKey: this.PlanningStatus.PlanningBehoefte },
                panelClass: 'planning-edit-dialog',
            });

            return;
        }

        this.subscriptions.add(
            this.dialog
                .open(TracklinePlanningEntityTypeDialogComponent)
                .afterClosed()
                .subscribe(entity => {
                    if (entity) {
                        this.dialog.open(TracklinePlanningEditDialogComponent, {
                            disableClose: true,
                            data: {
                                mainEntity: entity,
                                statusKey: this.PlanningStatus.PlanningBehoefte,
                            },
                            panelClass: 'planning-edit-dialog',
                        });
                    }
                })
        );
    }

    dragNeedEnded(event: CdkDragEnd) {
        const element = document
            .elementsFromPoint(event.dropPoint.x, event.dropPoint.y)
            .find(e => e.classList.contains('timeline'));
        if (element) {
            const rect = element.getBoundingClientRect();
            const relativeX = event.dropPoint.x - rect.left;
            const relativeY = event.dropPoint.y - rect.top;
            const component = this.dayComponents.find(d => formatDate(d.day.date, 'yyyy-MM-dd', 'nl') === element.id);
            component.planningFromNeed(event.source.data, relativeX, relativeY);
        }

        event.source.reset();
        const target = event.source.getRootElement();
        target.style.removeProperty('position');
        target.style.removeProperty('left');
        target.style.removeProperty('top');
    }

    mouseEnterFixDate(day: TracklinePlanningDay) {
        if (AuthorisationService.hasFeature(Features.PlanningFixedSave)) {
            this.weekPlanning.forEach(planningDay => {
                planningDay.fixed = planningDay.date.getTime() <= new Date(day.date).getTime();
            });
        }
    }

    mouseLeaveFixDate() {
        if (AuthorisationService.hasFeature(Features.PlanningFixedSave)) {
            this.weekPlanning.forEach(planningDay => {
                planningDay.fixed =
                    planningDay.date.getTime() <= new Date(this.fixedUntil).getTime() ||
                    Utils.isSpecificFixedDay(planningDay.date, this.fixedDays);
            });
        }
    }

    setFixed(day: TracklinePlanningDay) {
        if (this.mobile) {
            return;
        }

        if (AuthorisationService.hasFeature(Features.PlanningFixedSave)) {
            const ref = this.dialog.open(SetPlanningFixedDialogComponent, {
                data: day,
            });
            this.subscriptions.add(
                ref.afterOpened().subscribe(() => {
                    this.mouseEnterFixDate(day);
                })
            );
            const subs = ref.afterClosed().subscribe(() => {
                this.mouseLeaveFixDate();
                subs.unsubscribe();
            });
            this.subscriptions.add(subs);
        }
    }

    ngOnDestroy(): void {
        this.planningSubscriptions.unsubscribe();
        this.getDataSubscription?.unsubscribe();

        super.ngOnDestroy();
    }
}
